import { css } from 'lit';

/**
 * Design tokens as CSS custom properties.
 *
 * Values are distilled from www.pinakes.be's public Odoo theme (the `--pin-*` palette,
 * customer-tunable).
 * They are exposed under the `--sso-*` names used throughout the component code, with the original
 * `--pin-*` names kept as white-label aliases so a single `:root`/`:host` override re-skins the app.
 *
 * Because these are CSS custom properties they pierce Shadow DOM, so setting them once on the shell
 * (or `:root`) re-themes every element. The backend can inject a per-app / per-tenant brand by
 * overriding these on `<sso-app>` (see SSO-FRONTEND-LIT.md §5.3).
 */
export const tokens = css`
  :host {
    color-scheme: light;

    /* --- Brand palette from www.pinakes.be --- */
    --pin-brand: #072ac8; /* primary blue: buttons, links, focus */
    --pin-brand-hover: #0624aa;
    --pin-brand-active: #052096;
    --pin-accent: #e4ff1a; /* lime secondary/action accent */
    --pin-accent-hover: #e8ff3c;
    --pin-soft-blue: #7899d4; /* section headings, soft fills */
    --pin-brand-ink: #ffffff; /* text on the primary button */
    --pin-accent-ink: #212529; /* text on lime/soft-blue buttons */
    --pin-bg: #e9ecef; /* page background */
    --pin-surface: #ffffff; /* card / panel surface */
    --pin-surface-alt: #f8f9fa;
    --pin-ink: #212529; /* body text */
    --pin-muted: #6c757d; /* helper text, labels */
    --pin-border: #dee2e6; /* borders, separators */
    --pin-success: #28a745;
    --pin-warning: #ffc107;
    --pin-danger: #dc3545;
    --pin-radius: 4px; /* Odoo card/input radius */
    --pin-button-radius: 0; /* Pinakes "flat" buttons */
    --pin-appbar-bg: #ffffff;
    --pin-appbar-ink: var(--pin-brand);
    --pin-appbar-border: #e9ecef;

    /* --- Semantic aliases consumed by the components (--sso-*) --- */
    --sso-color-brand: var(--pin-brand);
    --sso-color-brand-hover: var(--pin-brand-hover);
    --sso-color-brand-active: var(--pin-brand-active);
    --sso-color-accent: var(--pin-accent);
    --sso-color-accent-hover: var(--pin-accent-hover);
    --sso-color-accent-ink: var(--pin-accent-ink);
    --sso-color-soft-blue: var(--pin-soft-blue);
    --sso-color-brand-ink: var(--pin-brand-ink);
    --sso-color-appbar-bg: var(--pin-appbar-bg);
    --sso-color-appbar-ink: var(--pin-appbar-ink);
    --sso-color-appbar-border: var(--pin-appbar-border);
    --sso-color-bg: var(--pin-bg);
    --sso-color-surface: var(--pin-surface);
    --sso-color-surface-alt: var(--pin-surface-alt);
    --sso-color-fg: var(--pin-ink);
    --sso-color-muted: var(--pin-muted);
    --sso-color-border: var(--pin-border);
    --sso-color-success: var(--pin-success);
    --sso-color-warning: var(--pin-warning);
    --sso-color-danger: var(--pin-danger);

    /* --- Geometry & spacing --- */
    --sso-radius: var(--pin-radius);
    --sso-button-radius: var(--pin-button-radius);
    --sso-space: 1rem;
    --sso-space-sm: 0.5rem;
    --sso-space-lg: 1.5rem;
    --sso-card-max: 420px; /* centered auth card max-width (mockups) */
    --sso-appbar-height: 64px;
    --sso-touch-target: 44px; /* minimum touch target (a11y) */

    /* --- Typography --- */
    --sso-font-body-family: 'Open Sans', 'Odoo Unicode Support Noto', system-ui, sans-serif;
    --sso-font-heading-family: 'Bitter', 'Odoo Unicode Support Noto', Georgia, serif;
    --sso-font-body: 400 1rem/1.5 var(--sso-font-body-family);
    --sso-font-h1: 600 1.5rem/1.25 var(--sso-font-heading-family);
    --sso-font-h2: 600 1.25rem/1.3 var(--sso-font-heading-family);
    --sso-font-mono: ui-monospace, 'SF Mono', 'Cascadia Code', Menlo, monospace;

    /* --- Effects --- */
    --sso-focus-ring: 0 0 0 3px color-mix(in srgb, var(--sso-color-brand) 40%, transparent);
    --sso-shadow-card: 0 1px 1px 0 rgba(0, 0, 0, 0.17);

    /* --- Branding assets --- */
    --sso-logo-url: url('/assets/brand/pinakes-logo-black.jpg');
  }
`;
